import * as React from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { Link } from '@helpcenter/analytics-components';
import { isRichTextAreaEnabled, isUiModificationsSupportEnabled } from '@helpcenter/feature-flags';
import { showSuccessFlag, showWarningFlag } from '@helpcenter/flags';
import { v4 as uuidv4 } from 'uuid';
import type {
	ForgeRequestCreatePropertyFormData,
	ForgeRequestCreatePropertyState,
} from '@helpcenter/forge-ui-utils';
import { SkeletonLoadingWrapper } from '@helpcenter/loading';
import { NavigationPrompt } from '@helpcenter/prompt';
import * as grid from '@helpcenter/styles/src/grid';
import { Formik } from 'formik';
import type { FormikProps } from 'formik';
import { isEqual as deepEquals, isEmpty, debounce, first, last, mapValues, map } from 'lodash';
import { injectIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { createRequest } from 'rest/request-create';
import type {
	FieldErrors,
	RequestDetailsResponse,
	SerializedProformaFormData,
} from 'rest/request-create';
import type { State } from 'state';
import { fetchRequestCreateAction } from 'state/actions/request-create';
import { shareRequest } from 'state/actions/request-details';
import type {
	FieldState,
	CmdbObjectPickerState,
	MultiServicePickerState,
} from 'state/persisted/request-create';
import { getContactLink, getXsrfToken } from 'state/selectors/env';
import {
	getCanAdministerProject,
	getIsProjectSimplified,
	getProjectKey,
} from 'state/selectors/portal';
import {
	getCanCreateUsers,
	getDateFormat,
	getDateTimeFormat,
	getProjectId,
	getRequestCreateOrError,
	getFirstDay,
	getUseISO8601WeekNumbers,
	getRequestTypeHasProformaForm,
	getIsProformaFormDirty,
	getIsPrefillPending,
} from 'state/selectors/request-create';
import { getAccountId, getCanAdministerJIRA, getLocale, isLoggedIn } from 'state/selectors/user';
import { DisableViewUntilMount } from 'view/common/disable-view-until-mount';
import { withConnectFieldSupport } from 'view/common/fields/connect-fields';
import { ForgeCustomFieldsResolver } from 'view/common/forge-custom-fields/forge-custom-fields-resolver';
import RequestDetailsStateless from 'view/request-details/request-details-stateless';
import withAnalyticsContext from '@atlaskit/analytics-next/withAnalyticsContext';
import withAnalyticsEvents from '@atlaskit/analytics-next/withAnalyticsEvents';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { FormHeader } from '@atlaskit/form/form-header';
import { RequiredAsterisk } from '@atlaskit/form/required-asterisk';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/box';
import { Flex } from '@atlaskit/primitives/components/flex';
import { Inline } from '@atlaskit/primitives/inline';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
// eslint-disable-next-line rulesdir/fedramp-isolated-cloud
import { isFedRamp } from '@atlassian/atl-context';
import { getIsHelpHub } from '@atlassian/help-center-common-util/advanced-help-center';
import { getAccountIdsFromComment, parseAdf } from '@atlassian/help-center-common-util/adf';
import {
	firstInteraction,
	AnalyticsActionSubjects,
	trackError,
	trackIssueEventNext,
	trackRequestCreateNonGenericErrors,
} from '@atlassian/help-center-common-util/analytics';
import { getIssueAnalyticsContext } from '@atlassian/help-center-common-util/analytics/issue-context';
import {
	ProformaSerializationTraceStep,
	traceProformaSerialization,
	type ProformaSerializationTraceAttributes,
} from '@atlassian/help-center-common-util/analytics/proforma-serialization-trace';
import { componentWithCondition } from '@atlassian/help-center-common-util/feature-flags-utils';
import { history } from '@atlassian/help-center-common-util/history';
import * as qs from '@atlassian/help-center-common-util/history-qs';
import { search } from '@atlassian/help-center-common-util/location';
import { toNumber, toString } from '@atlassian/help-center-common-util/number';
import { PushRedirect, PushRedirectWithState } from '@atlassian/help-center-common-util/redirect';
import { UiModificationsFormValuesApplier } from '@atlassian/jsm-ui-modifications-view-request-create/src/ui/form-value-applier';
import { UiModificationsSectionsMessages } from '@atlassian/jsm-ui-modifications-view-request-create/src/ui/ui-modifications-sections-messages';
import { withUiModificationsFormDecorator } from '@atlassian/jsm-ui-modifications-view-request-create/src/ui/with-form-decorator';
import { REFERRER_QUERY_PARAM } from '@atlassian/learn-by-doing-create-request';
import type { TemplateForm } from '@atlassian/proforma-common-core/form';
import type {
	RegisterProformaForm,
	ValidateProformaForm,
	SerializeProformaForm,
} from '@atlassian/proforma-request-create';
import { canNavigateBackFromRequestCreate } from '../../utils/request-create-previous-location';
import { notifyRovoRequestCreated } from '../../utils/rovo-integration';
import {
	FORM_SUBMISSION_RE_SUBMIT_AND_PROCEED,
	FORM_SUBMISSION_PROCEED,
} from '../common/forge-custom-fields/constants';
import { SimpleIconPage } from '../layout/simple-icon-page';
import messages from './messages';
import FormDumb from './request-create-form-dumb/request-create-form-dumb';
import { RequestTypeProformaError } from './request-type-proforma-error';
import type { FormErrors, FormInput, RequestCreateFormWithHOCsProps } from './types';
import type { ErrorWithReason } from 'rest/request-create/types';
import type {
	ForgeCustomFieldsResolverStatus,
	ResolveForgeCustomFields,
} from 'view/common/forge-custom-fields/types';

interface CompleteData {
	issueKey: string;
	email: string | undefined;
}

interface FormState {
	errors: FormErrors | undefined;
	submitting: boolean;
	submitDisabled: boolean;
	submissionErrorCount: number;
	isDirty: boolean;
	complete?: CompleteData;
	isFormExpanded: boolean;
	isAttachmentUploading: boolean;
	resolveForgeCustomFields: ResolveForgeCustomFields;
	failedOrTimedOutForgeCustomFields: string[];
	forgeFormData: ForgeRequestCreatePropertyState;
}

/** This component should not fetch any data. Data should be fetched already by the parent component. */
export class RequestCreateForm extends React.Component<RequestCreateFormWithHOCsProps, FormState> {
	static DIRTY_CHECK_DEBOUNCE_MS = 100;

	state: FormState = {
		errors: undefined,
		submitting: false,
		submitDisabled: false,
		submissionErrorCount: 0,
		isDirty: false,
		isFormExpanded: true,
		isAttachmentUploading: false,
		resolveForgeCustomFields: () => Promise.resolve(FORM_SUBMISSION_PROCEED),
		failedOrTimedOutForgeCustomFields: [],
		forgeFormData: {},
	};

	isUnmounted: boolean = false;

	formRef = React.createRef<HTMLFormElement>();

	/** Correlation id generated per submit attempt (set at the start of onSubmit). */
	formSubmissionId: string | undefined = undefined;

	private proformaSerializationTraceAttributes: ProformaSerializationTraceAttributes = {};

	private validateProformaForm: ValidateProformaForm;

	private serializeProformaForm: SerializeProformaForm;

	/** Shared trace attributes derived from props, attached to every trace event. */
	getProformaTraceBaseAttributes = () => ({
		context_formSubmissionId: this.formSubmissionId,
		context_hasProformaForm: this.props.hasProformaForm,
		context_portalId: this.props.portalId,
		context_requestTypeId: this.props.requestTypeId,
		context_projectId: this.props.projectId,
	});

	validateProformaFormFields = (): boolean => {
		let isValid = true;
		const validateFnPresent = Boolean(this.validateProformaForm);
		this.validateProformaForm &&
			this.validateProformaForm((isFormValid) => {
				isValid = isFormValid;
			});
		this.proformaSerializationTraceAttributes = {
			...this.proformaSerializationTraceAttributes,
			validateForm_validateFnPresent: validateFnPresent,
			validateForm_isProformaFormValid: isValid,
		};
		return isValid;
	};

	serializeProformaFormFields = (): SerializedProformaFormData | undefined => {
		let serializedAnswer: SerializedProformaFormData | undefined = undefined;
		let serializedAnswerCount = 0;
		let visibleFieldCount: number | undefined;
		const serializeFnPresent = Boolean(this.serializeProformaForm);
		this.serializeProformaForm &&
			this.serializeProformaForm((serializedData) => {
				serializedAnswer = serializedData;
				visibleFieldCount = serializedData?.visibleFieldCount;
				serializedAnswerCount = serializedData?.answers
					? Object.keys(serializedData.answers).length
					: 0;
			});
		this.proformaSerializationTraceAttributes = {
			...this.proformaSerializationTraceAttributes,
			serializeForm_serializedAnswerCount: serializedAnswerCount,
			serializeForm_visibleFieldCount: visibleFieldCount,
			serializeForm_serializeFnPresent: serializeFnPresent,
			serializeForm_serializedResultPresent: Boolean(serializedAnswer),
		};
		return serializedAnswer;
	};

	checkIfFormIsDirty = debounce(
		(values: FormInput, initialValues: FormInput, fields: FieldState[]) => {
			const copyValues = { ...values };
			const copyIntitalValues = { ...initialValues };
			const clearValues = (fieldId: string) => {
				delete copyValues[fieldId];
				delete copyIntitalValues[fieldId];
			};
			// Organisation picker and rich text area is added to the values after page load so we need
			// to remove these for a deep equality check as it doesnt appear in the initialValues
			for (const f of fields) {
				if (f.type === 'organisationpicker') {
					delete copyValues[f.id];
					delete copyIntitalValues[f.id];
				}
				if (isRichTextAreaEnabled() && f.type === 'textarea') {
					// Note: This will not render a dirty form warning when users replace rich text area default values with content of the same length
					// object property order is not guaranteed to be the same in the stringified ADF values when they update so we can't do a simple equality check.
					// this is preferred to alternatives like performing a deepEquals on the ADF structure, or normalizing the stringified ADF before comparing them
					// because this is on a performance sensitive rendering path, and will work as expected for the vast majority of cases
					// Suppressing existing violation. Please fix this.
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					const value = copyValues[f.id] ?? '';
					// Suppressing existing violation. Please fix this.
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					const initial = copyIntitalValues[f.id] ?? '';
					// TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
					if (initial.length === value.length) {
						clearValues(f.id);
					}
				}
			}

			// Remove recaptcha value when checking form is dirty - we don't want it to come up if they want
			// to leave.
			delete copyValues['g-recaptcha-response'];

			const isDirty = !deepEquals(copyIntitalValues, copyValues);

			// While this isn't explicitly tested, it will result in an infinite loop without it.
			// Don't remove!
			if (this.state.isDirty !== isDirty) {
				this.setState({
					isDirty,
				});
			}
		},
		RequestCreateForm.DIRTY_CHECK_DEBOUNCE_MS,
	);

	componentWillUnmount() {
		this.isUnmounted = true;
	}

	componentDidMount() {
		const fieldConfigErrors = this.getInvalidConfigFieldErrors();
		if (!isEmpty(fieldConfigErrors)) {
			this.setState((previousState) => ({
				errors: {
					...previousState.errors,
					fieldErrors: fieldConfigErrors,
				},
			}));
		}
	}

	onToggleFormExpanded = () => {
		this.setState((prevState) => ({
			isFormExpanded: !prevState.isFormExpanded,
		}));
	};

	getFieldErrors = (formInput: FormInput): FieldErrors => {
		const emptyRequiredFieldErrors = this.getEmptyRequiredFieldErrors(formInput);

		// Insight/CMDB field errors
		const cmdbOverLimitFieldErrors = this.getInsightFieldLimitErrors(formInput);
		const cmdbInvalidConfigFieldErrors = this.getInvalidConfigFieldErrors();

		// Affected Service field errors
		const affectedServiceFieldLimitErrors = this.getAffectedServicesFieldLimitErrors(formInput);

		return {
			...emptyRequiredFieldErrors,
			...cmdbOverLimitFieldErrors,
			...cmdbInvalidConfigFieldErrors,
			...affectedServiceFieldLimitErrors,
		};
	};

	ifForgeFormValid = (forgeData: ForgeRequestCreatePropertyState): boolean => {
		const forgeDataKeys = Object.keys(forgeData);
		return forgeDataKeys.filter((keys) => forgeData[keys].isValid).length === forgeDataKeys.length;
	};

	getRovoAnalyticsAttributes = (formInput: FormInput, searchParams: URLSearchParams) => {
		if (!fg('jsm_portal_rovo_integration')) {
			return {};
		}
		const prefillOverrides = this.props.formData?.urlParamFieldValueOverrides ?? {};
		const prefillFieldIds = Object.keys(prefillOverrides);
		const prefillFieldCount = prefillFieldIds.length;
		const isPrefillTriggeredByRovo = searchParams.has('rovoChatTriggeringAction');
		const prefillModifiedByUser = prefillFieldIds.some((fieldId) => {
			// If the field was not submitted (e.g. conditionally hidden), treat it as unmodified
			if (!(fieldId in formInput)) {
				return false;
			}
			// Suppressing existing violation. Please fix this.
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			const prefillValue = prefillOverrides[fieldId];
			// Suppressing existing violation. Please fix this.
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			const submittedValue = formInput[fieldId];
			return JSON.stringify(prefillValue) !== JSON.stringify(submittedValue);
		});
		return {
			isPrefillTriggeredByRovo,
			prefillFieldCount,
			prefillFieldIds,
			prefillModifiedByUser,
		};
	};

	hasRequiredFields = () => {
		const { formData } = this.props;

		return !!formData?.fields?.find((f) => f.displayed && f.required);
	};

	hasRequiredFieldsInProformaForm = () => {
		const { formData } = this.props;
		const proformaForm = formData?.proformaTemplateForm;
		const formQuestions = (proformaForm as TemplateForm)?.design?.questions;
		let hasRequiredFields = false;
		for (const questionKey in formQuestions) {
			if (formQuestions[questionKey]?.validation?.rq) {
				hasRequiredFields = true;
				break;
			}
		}
		return hasRequiredFields;
	};
	getEmptyRequiredFieldErrors = (formInput: FormInput): FieldErrors => {
		const { formData, intl } = this.props;
		const requiredFields = formData?.fields?.filter((f) => f.displayed && f.required);

		const invalidFields = Object.keys(formInput).reduce((fields: FieldState[], fieldId) => {
			const invalidField = requiredFields?.find((f) => {
				// Suppressing existing violation. Please fix this.
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				const value = formInput[fieldId];
				// check if value of form field is an empty array, it is an invalid value for a required field.
				if (Array.isArray(value)) {
					return f.id === fieldId && value.length === 0;
				}
				return f.id === fieldId && !Boolean(value);
			});
			return invalidField ? fields.concat(invalidField) : fields;
		}, []);

		return invalidFields.reduce((errors, field) => {
			const invalidField = invalidFields.find((f) => f.id === field.id);
			if (invalidField) {
				// @ts-ignore TS(7053) TypeScript upgrade 5.1.6, please fix this violation when you revisit this code.: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
				errors[invalidField.id] = intl.formatMessage(messages.fieldValidationError, {
					fieldLabel: field?.label ?? '',
				});
			}
			return errors;
		}, {});
	};

	getAffectedServicesFieldLimitErrors = (formInput: FormInput): FieldErrors => {
		const {
			formData,
			intl: { formatMessage },
		} = this.props;

		const hasExceededLimit = (field: FieldState): field is MultiServicePickerState =>
			field.type === 'multiservicepicker' &&
			Boolean(field.displayed) &&
			// TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			formInput[field.id]?.length > field.limit;

		const limitExceededAffectedServiceFields = formData?.fields?.filter(hasExceededLimit) ?? [];

		return limitExceededAffectedServiceFields.reduce<FieldErrors>(
			(total, current) => ({
				...total,
				[current.id]: formatMessage(messages.affectedServicesFieldLimitError, {
					serviceLimit: current.limit,
				}),
			}),
			{},
		);
	};

	getInsightFieldLimitErrors = (formInput: FormInput): FieldErrors => {
		const { formData, intl } = this.props;

		// Filter the field type to be Cmdb Object Picker
		// and whether the cmdb object picker has exceeded its limit
		const hasExceededLimit = (field: FieldState): field is CmdbObjectPickerState => {
			return (
				field.type === 'cmdbobjectpicker' &&
				Boolean(field.displayed) &&
				// TypeScript upgrade (v4.4.3). Please correct when you revisit this code.
				// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
				formInput[field.id]?.length > field.limit
			);
		};
		const limitExceededCmdbFields: CmdbObjectPickerState[] | undefined =
			formData?.fields?.filter<CmdbObjectPickerState>(hasExceededLimit) ?? [];

		return limitExceededCmdbFields.reduce((errors: FieldErrors, field): FieldErrors => {
			errors[field.id] = intl.formatMessage(messages.fieldLimitExceededError, {
				objectLimit: field.limit,
			});
			return errors;
		}, {});
	};

	getInvalidConfigFieldErrors = (): FieldErrors => {
		const { formData, intl } = this.props;

		// Filter the field type to be cmdb object picker
		// and whether the REQUIRED cmdb object picker has a valid configuration
		const hasInvalidFieldConfiguration = (field: FieldState): field is CmdbObjectPickerState => {
			return (
				field.type === 'cmdbobjectpicker' &&
				Boolean(field.required) &&
				Boolean(field.displayed) &&
				!field.hasValidConfig
			);
		};
		const invalidCmdbFields: CmdbObjectPickerState[] =
			formData?.fields?.filter<CmdbObjectPickerState>(hasInvalidFieldConfiguration) ?? [];

		return invalidCmdbFields.reduce((errors: FieldErrors, field): FieldErrors => {
			errors[field.id] = intl.formatMessage(messages.fieldInvalidConfigError);
			return errors;
		}, {});
	};

	shareRequestWithMentionedUsers = (
		completeData: CompleteData,
		users: [string, string][] | undefined,
		shareRequestAction: typeof shareRequest,
		reporterId: string,
	): void => {
		// add the share event for the addParticipants
		const usersWithoutReporter = users?.filter(([userId]) => userId !== reporterId);
		const idsOnly = usersWithoutReporter?.map(([userId]) => userId) || [];
		const usersLength = usersWithoutReporter?.length;
		const isSingleUser = usersLength === 1;
		const firstUser = first(usersWithoutReporter);
		const firstUserName = last(firstUser);
		if (usersLength) {
			shareRequestAction(
				completeData.issueKey,
				[],
				[],
				idsOnly,
				true,
				isSingleUser ? firstUserName : undefined,
				true,
			);
		}
	};

	getRichTextAnalytics = (formInput: FormInput) => {
		const { formData } = this.props;
		const richTextFields = formData?.fields.filter(
			(field) => field.type === 'textarea' && field.rendererType === 'atlassian-wiki-renderer',
		);
		const formFields = Object.keys(formInput);
		const filledFields = richTextFields?.filter(
			(field) => formFields.includes(field.id) && !!formInput[field.id],
		);

		return {
			shown: richTextFields?.length || 0,
			filled: filledFields?.length || 0,
		};
	};

	getEntitlementPickerAnalytics = (formInput: FormInput) => {
		const entitlementField = this.props.formData?.fields.find(
			(field) => field.type === 'entitlementpicker',
		);

		return {
			shown: !!entitlementField,
			filled: !!entitlementField && !!formInput[entitlementField?.id],
		};
	};

	getUserMentions = (formInput: FormInput): [string, string][] | undefined => {
		const adfFields = this.props.formData?.fields.filter(
			(field) => field.type === 'textarea' && field.rendererType === 'atlassian-wiki-renderer',
		);

		try {
			const accountIds = adfFields?.reduce((accIds: [string, string][], field: FieldState) => {
				// Suppressing existing violation. Please fix this.
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				const content = formInput[field.id];
				if (!content) return accIds;
				const adfFieldContent = parseAdf(content);
				const userIds = Array.from(getAccountIdsFromComment(adfFieldContent));
				return accIds.concat(userIds);
			}, []);
			return accountIds;
		} catch {
			return [];
		}
	};

	onSubmit = async (formInput: FormInput) => {
		const {
			portalId,
			requestTypeId,
			xsrfToken,
			projectId,
			contactLink,
			userAuthenticated,
			analyticsPayload,
			isProjectAdmin,
			isProjectSimplified,
			projectKey,
			intl,
			shareRequestAction,
			createAnalyticsEvent,
			showSuccessFlagAction,
			showWarningFlagAction,
		} = this.props;

		this.formSubmissionId = uuidv4();
		this.proformaSerializationTraceAttributes = {
			registerProformaForm_invoked:
				this.proformaSerializationTraceAttributes.registerProformaForm_invoked,
			renderForm_completed: this.proformaSerializationTraceAttributes.renderForm_completed,
		};
		traceProformaSerialization(ProformaSerializationTraceStep.PreSubmit, {
			...this.getProformaTraceBaseAttributes(),
			...this.proformaSerializationTraceAttributes,
		});

		this.setState({ submitting: true });
		const resolveStatus = await this.resolveForgeCustomFields();
		if (resolveStatus === FORM_SUBMISSION_RE_SUBMIT_AND_PROCEED) {
			// Re-submitting the form this way to have function arguments updated with the latest values.
			this.formRef?.current?.requestSubmit();
			return;
		}

		const fieldErrors = this.getFieldErrors(formInput);
		const isFormValid = isEmpty(fieldErrors) && this.ifForgeFormValid(this.state.forgeFormData);
		const richTextAnalyticsPayload = isRichTextAreaEnabled()
			? this.getRichTextAnalytics(formInput)
			: undefined;
		const entitlementPickerAnalyticsPayload = this.getEntitlementPickerAnalytics(formInput);
		const searchParams = new URLSearchParams(search());

		const isProformaFormValid = this.validateProformaFormFields();

		if (!isFormValid) {
			this.setState((previousState: Partial<FormState>) => ({
				submitting: false,
				errors: {
					...previousState.errors,
					fieldErrors,
				},
			}));

			this.focusFirstErrorField(fieldErrors);
			return;
		}

		if (!isProformaFormValid) {
			this.setState({
				submitting: false,
			});

			this.focusElement('[data-testid="proforma-form"]', { useSelector: true });
			return;
		}

		if (portalId && requestTypeId && xsrfToken && projectId) {
			this.setState({
				submitting: true,
			});

			try {
				const connectRequestProperties =
					(this.props.onSubmit && (await this.props.onSubmit())) || [];
				const forgeRequestProperties: ForgeRequestCreatePropertyState[] = map(
					this.state.forgeFormData,
					(value, key) => ({ [key]: value }),
				);

				const requestProperties = [...connectRequestProperties, ...forgeRequestProperties];
				let serializedRequestProperties;
				try {
					serializedRequestProperties = JSON.stringify(
						requestProperties.reduce(
							(acc: object, data: object) => ({ ...acc, ...mapValues(data, JSON.stringify) }),
							{},
						),
					);
				} catch (error) {
					trackError('Failed to serialize request properties.', {}, error);
				}

				const newFormInputs = {
					...formInput,
					...(requestProperties.length && { requestProperties: serializedRequestProperties }),
				};

				const serializedProformaFormData = this.serializeProformaFormFields();
				const proformaFormDataForRequest = serializedProformaFormData
					? {
							answers: serializedProformaFormData.answers,
							templateFormId: serializedProformaFormData.templateFormId,
						}
					: undefined;

				// If a Proforma form is expected but the serialized output is empty, the form data
				// is silently omitted from the outgoing request body. Include the parent-owned
				// diagnostics only for this unexpected failure.
				if (this.props.hasProformaForm && !serializedProformaFormData) {
					traceProformaSerialization(ProformaSerializationTraceStep.PostSerialization, {
						...this.getProformaTraceBaseAttributes(),
						...this.proformaSerializationTraceAttributes,
					});
				}

				const {
					issue,
					issueType,
					requestDetailsBaseUrl,
					key: issueKey,
					// Upgrading @atlaskit/platform; fix these violations.
				} = (await createRequest(
					portalId,
					requestTypeId,
					projectId,
					xsrfToken,
					newFormInputs,
					proformaFormDataForRequest,
				)) as RequestDetailsResponse;

				if (fg('jsm_portal_rovo_integration') && searchParams.has('rovoChatTriggeringAction')) {
					// Notify Rovo Chat about successful request creation
					const portalRequestURL = `${window.location.origin}${requestDetailsBaseUrl}`;
					notifyRovoRequestCreated(
						portalRequestURL,
						searchParams.get('rovoChatTriggeringAction') || '',
					);
				}

				// Suppressing existing violation. Please fix this.

				const completeData: CompleteData = {
					issueKey,
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					email: userAuthenticated ? undefined : formInput.email,
				};
				let analyticsMentions;
				// we send the participants to request details page only when the user is logged in
				if (userAuthenticated) {
					const usersMentionedInDescription = this.getUserMentions(formInput);
					this.shareRequestWithMentionedUsers(
						completeData,
						usersMentionedInDescription,
						shareRequestAction,
						formInput?.reporter,
					);
					analyticsMentions = {
						mentionedUsersLength: usersMentionedInDescription?.length,
					};
				}
				this.props.notifySuccess();
				if (showSuccessFlagAction) {
					if (this.state.failedOrTimedOutForgeCustomFields.length > 0) {
						showWarningFlagAction(
							messages.requestSubmittedFcfWarningTitle,
							messages.requestSubmittedFcfWarningMessageDescription,
							undefined,
							undefined,
							{ field: this.state.failedOrTimedOutForgeCustomFields.join(', ') },
						);
					} else if (projectKey && issueKey && isProjectAdmin && !isProjectSimplified) {
						showSuccessFlagAction(
							messages.requestSubmittedMessage,
							{},
							messages.requestSubmittedDescription,
							[
								{
									contentNode: (
										<Link
											// eslint-disable-next-line rulesdir/do-not-manually-build-jsm-agent-view-url
											href={`/jira/servicedesk/projects/${projectKey}/issue/${issueKey}`}
											target="_blank"
											actionSubjectId="viewWorkItemFromPortalSuccessPopup"
										>
											<Inline space="space.050" alignBlock="center">
												{intl.formatMessage(messages.requestSubmittedAction)}
												<LinkExternalIcon label={''} />
											</Inline>
										</Link>
									),
								},
							],
						);
					} else {
						showSuccessFlagAction(messages.requestSubmittedMessage);
					}
				}

				if (createAnalyticsEvent) {
					const rovoAnalyticsAttributes = this.getRovoAnalyticsAttributes(formInput, searchParams);

					trackIssueEventNext(
						{
							projectId,
							issueId: issue.id,
							action: 'created',
							attributes: {
								...rovoAnalyticsAttributes,
								...analyticsMentions,
								requestTypeId: String(requestTypeId),
								portalId: String(portalId),
								issueTypeId: String(issueType),
								richTextEditorShown: richTextAnalyticsPayload?.shown,
								richTextEditorFilled: richTextAnalyticsPayload?.filled,
								entitlementPickerShown: entitlementPickerAnalyticsPayload?.shown,
								entitlementPickerFilled: entitlementPickerAnalyticsPayload?.filled,
								createdWithinEmbedWindow: this.props.isEmbeddedRoute,
								requestFormSource: searchParams.get('source') ?? '',
								hasProformaForm: this.props.hasProformaForm,
							},
						},
						analyticsPayload.user,
						createAnalyticsEvent,
						analyticsPayload.portal,
					);
				}

				this.setState({
					complete: completeData,
				});
			} catch (errors) {
				const {
					submissionError,
					traceId,
					fieldErrors: fieldErrorsReceived,
				} = errors as {
					submissionError: ErrorWithReason;
					fieldErrors: FieldErrors;
					traceId: string;
				};
				const shouldNotifyFAMAFailure = submissionError?.isGeneric;
				if (shouldNotifyFAMAFailure) {
					if (submissionError?.statusCode) {
						this.props.notifyFailure(
							`Failed to submit form (${String(submissionError.statusCode).charAt(
								0,
							)}xx error): ${JSON.stringify({ submissionError, fieldErrors: fieldErrorsReceived })}`,
							traceId,
						);
					} else {
						this.props.notifyFailure(
							`Unknown Exception: Failed to submit form (5xx error): ${JSON.stringify({
								submissionError,
								fieldErrors: fieldErrorsReceived,
							})}`,
							traceId,
						);
					}
				} else {
					trackRequestCreateNonGenericErrors(errors, createAnalyticsEvent);
				}

				if (!this.isUnmounted) {
					// We use this count as a unique key to make sure the error message autofocuses on every error
					this.setState((previousState) => ({
						submissionErrorCount: submissionError
							? previousState.submissionErrorCount + 1
							: previousState.submissionErrorCount,
						// Suppressing existing violation. Please fix this.
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						errors: { ...errors, contactLink },
						submitting: false,
						isFormExpanded: true,
					}));
				}
			}

			this.props.onSubmitComplete && this.props.onSubmitComplete();
		}
	};

	disableSubmitButton = () => {
		this.setState({ submitDisabled: true });
	};

	preloadRequestDetails = () => {
		// this check is added to fix the chunk load issue in fedramp environment for preloaded script
		if (isFedRamp && isFedRamp()) {
			return;
		}
		RequestDetailsStateless.preload();
	};

	enableSubmitButton = () => {
		this.setState({ submitDisabled: false });
	};

	blockFormSubmission = (blocking: boolean) => {
		this.setState({ isAttachmentUploading: blocking });
	};

	registerProformaForm: RegisterProformaForm = (validateProformaForm, serializeProformaForm) => {
		this.validateProformaForm = validateProformaForm;
		this.serializeProformaForm = serializeProformaForm;
		this.proformaSerializationTraceAttributes = {
			...this.proformaSerializationTraceAttributes,
			registerProformaForm_invoked: true,
		};
	};

	onProformaFormRendered = () => {
		this.proformaSerializationTraceAttributes = {
			...this.proformaSerializationTraceAttributes,
			renderForm_completed: true,
		};
	};

	setResolveForgeCustomFields = (fn: ResolveForgeCustomFields) => {
		this.setState({ resolveForgeCustomFields: fn });
	};

	setFailedOrTimedOutForgeCustomFields = (fields: string[]) => {
		this.setState({ failedOrTimedOutForgeCustomFields: fields });
	};

	resolveForgeCustomFields = async (): Promise<ForgeCustomFieldsResolverStatus> => {
		return this.state.resolveForgeCustomFields();
	};

	/**
	 * Focuses an element by ID or selector, with automatic retry logic.
	 * Uses the form ref to scope queries (more React-idiomatic than document queries).
	 * Uses requestAnimationFrame to sync with browser paint cycles instead of arbitrary timeouts.
	 */
	focusElement = (
		identifier: string,
		options: {
			useSelector?: boolean;
			retries?: number;
			scrollBehavior?: ScrollBehavior;
			scrollBlock?: ScrollLogicalPosition;
		} = {},
	) => {
		const {
			useSelector = false,
			retries = 2,
			scrollBehavior = 'smooth',
			scrollBlock = 'center',
		} = options;

		const focusAndScroll = (element: HTMLElement) => {
			element.focus();
			element.scrollIntoView({ behavior: scrollBehavior, block: scrollBlock });
		};

		const attemptFocus = (retriesLeft: number) => {
			let element: HTMLElement | null = null;

			// Try to find element within the form first (scoped to our component)
			if (this.formRef.current) {
				element = useSelector
					? (this.formRef.current.querySelector(identifier) as HTMLElement)
					: this.formRef.current.querySelector(`#${identifier}`);
			}

			if (element) {
				focusAndScroll(element);
				return;
			}

			// Retry if element not yet in DOM (for slow renders)
			if (retriesLeft > 0) {
				requestAnimationFrame(() => attemptFocus(retriesLeft - 1));
			}
		};

		// Use requestAnimationFrame to wait for next paint cycle
		requestAnimationFrame(() => attemptFocus(retries));
	};

	focusFirstErrorField = (fieldErrors: FieldErrors) => {
		const firstErrorFieldId = Object.keys(fieldErrors)?.[0];
		if (!firstErrorFieldId) {
			return;
		}

		this.focusElement(firstErrorFieldId);
	};

	renderForm = ({ values, initialValues, setFieldValue, handleSubmit }: FormikProps<object>) => {
		di(UiModificationsFormValuesApplier, ForgeCustomFieldsResolver);
		const {
			formData,
			onFieldChangedByUser,
			accountId,
			portalId,
			requestTypeId,
			projectId,
			canCreateUsers,
			dateFormat,
			dateTimeFormat,
			firstDay,
			useISO8601WeekNumbers,
			isDirtyCheckDisabled,
			hasProformaForm,
			proformaFormIsDirty,
			intl,
			locale,
		} = this.props;
		// TODO: Move this into <Formik /> when upgraded to formik@1.x.x
		// See: https://github.com/jaredpalmer/formik#the-gist
		const { errors, submitting, submitDisabled, submissionErrorCount, isAttachmentUploading } =
			this.state;

		if (!portalId || !requestTypeId || !formData) {
			return null;
		}

		const { fields, form, knowledgeBase } = formData;

		if (!form) {
			return null;
		}

		if (fields !== undefined) {
			this.checkIfFormIsDirty(values, initialValues, fields);
		}

		// We use any for value here to maintain consistency with the type signature for setFieldValue

		const onFieldChange = (
			field: string,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			value: any,
			notFromUserInteraction?: boolean,
			additionalFieldData?: { rawObject: object; rawValue?: unknown },
		) => {
			if (!notFromUserInteraction) {
				firstInteraction({
					actionSubject: AnalyticsActionSubjects.FORM,
					source: 'portalScreen',
					isNewPortal: true,
				});

				// on user interaction, clear Forge UIM field override
				onFieldChangedByUser?.({
					field,
					notFromUserInteraction,
					additionalFieldData,
					value: value as unknown,
				});
			}

			return setFieldValue(field, value);
		};

		const onCancel = () => {
			const cancelPath = getIsHelpHub() ? `/portal/${portalId}` : `/portal/${portalId}/group/-1`;

			if (getIsHelpHub() && canNavigateBackFromRequestCreate()) {
				history.goBack();
				return;
			}

			history.push(cancelPath);
		};

		const updateForgeRequestPropertyData = (payload: ForgeRequestCreatePropertyFormData) => {
			this.setState((prevState) => ({
				forgeFormData: {
					...prevState.forgeFormData,
					[payload.extensionId]: payload.data,
				},
			}));
		};
		const preventNavigation = (this.state.isDirty || proformaFormIsDirty) && !isDirtyCheckDisabled;

		return (
			<>
				{isUiModificationsSupportEnabled() && (
					<>
						<UiModificationsSectionsMessages />
						<UiModificationsFormValuesApplier
							fieldValues={values}
							setFieldValue={setFieldValue}
							fields={fields}
						/>
					</>
				)}
				<ForgeCustomFieldsResolver
					setResolve={this.setResolveForgeCustomFields}
					setFailedOrTimedOut={this.setFailedOrTimedOutForgeCustomFields}
					requestTypeId={requestTypeId}
					fields={fields}
				/>
				<FormDumb
					accountId={accountId}
					canCreateUsers={canCreateUsers}
					projectId={projectId}
					portalId={portalId}
					requestTypeId={requestTypeId}
					knowledgeBase={knowledgeBase}
					fields={fields}
					fieldValues={values}
					urlParamFieldValueOverrides={formData.urlParamFieldValueOverrides}
					onFieldChange={onFieldChange}
					onFieldBlurredByUser={this.props.onFieldBlurredByUser}
					onCancel={onCancel}
					onSubmit={handleSubmit}
					errors={errors}
					submissionErrorCount={submissionErrorCount}
					submitting={submitting}
					submitDisabled={submitDisabled}
					captchaEnabled={form.captchaEnabled}
					sitekey={form.sitekey}
					enableSubmitButton={this.enableSubmitButton}
					disableSubmitButton={this.disableSubmitButton}
					onHoverSubmitButton={this.preloadRequestDetails}
					onToggleFormExpanded={this.onToggleFormExpanded}
					isFormExpanded={this.state.isFormExpanded}
					dateFormat={dateFormat}
					dateTimeFormat={dateTimeFormat}
					firstDay={firstDay}
					useISO8601WeekNumbers={useISO8601WeekNumbers}
					isAttachmentUploading={isAttachmentUploading}
					blockFormSubmission={this.blockFormSubmission}
					registerProformaForm={hasProformaForm ? this.registerProformaForm : undefined}
					onProformaFormRendered={this.onProformaFormRendered}
					locale={locale ? locale : intl.locale}
					updateForgeRequestPropertyData={updateForgeRequestPropertyData}
					formRef={this.formRef}
					proformaField={this.props.proformaField}
				/>
				<NavigationPrompt preventNavigation={preventNavigation} />
			</>
		);
	};

	renderAnonymousRedirect() {
		const { complete } = this.state;
		const { portalId, requestTypeId } = this.props;
		return (
			<PushRedirectWithState
				// TypeScript upgrade (v4.4.3). Please correct when you revisit this code. Please correct when this code is revisited.
				// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
				to={`/portal/${portalId}/create/${requestTypeId}/confirm`}
				state={{ email: complete?.email, issueKey: complete?.issueKey }}
			/>
		);
	}

	render() {
		di(Formik);

		const {
			formData,
			portalId,
			requestTypeId,
			userAuthenticated,
			canAdministerJira,
			isEmbeddedRoute,
			intl,
			error,
			isPrefillPending,
		} = this.props;

		const referrer = qs.get(REFERRER_QUERY_PARAM);
		const shouldAddReferrer = referrer && referrer !== 'undefined';
		// TODO: Move this into <Formik /> when upgraded to formik@1.x.x
		// See: https://github.com/jaredpalmer/formik#the-gist
		const { complete } = this.state;

		if (!portalId || !requestTypeId) {
			return null;
		}

		// Show UFOHold when formData is not loaded yet (no error)
		if (!formData && !error) {
			return (
				<Root>
					<UFOLoadHold name="request-create-form-loading">
						<SkeletonLoadingWrapper isLoading />
					</UFOLoadHold>
				</Root>
			);
		}

		// When jsm_portal_rovo_integration is on and there are URL params for pre-filling,
		// we delay rendering the form until the epic has resolved all field values.
		// This prevents Formik from initialising with empty values before pre-fill data arrives.
		// We only do this when pre-fill is actually being used to avoid impacting the normal flow.
		if (fg('jsm_portal_rovo_integration') && isPrefillPending) {
			return (
				<Root>
					<SkeletonLoadingWrapper isLoading />
				</Root>
			);
		}

		// If the submission is complete we will direct the user to the next page,
		// after checking if user is authenticated and
		// portal is rendered inside an embeddable route or not
		if (complete) {
			if (userAuthenticated) {
				return isEmbeddedRoute ? (
					<Root>
						<SimpleIconPage
							actionHrefTarget="_blank"
							title={intl.formatMessage(messages.thanksTitle)}
							description={intl.formatMessage(messages.referenceDescription)}
							callToActionHref={`/portal/${portalId}/${complete.issueKey}${
								shouldAddReferrer ? `?${REFERRER_QUERY_PARAM}=${referrer}` : ''
							}`}
							callToActionText={intl.formatMessage(messages.callToAction)}
						/>
					</Root>
				) : (
					<Root>
						<PushRedirect
							to={qs.addParamsToUrl(`/portal/${portalId}/${complete.issueKey}`, {
								created: 'true',
								...(shouldAddReferrer && {
									[REFERRER_QUERY_PARAM]: referrer,
								}),
							})}
						/>
					</Root>
				);
			}
			return <Root>{this.renderAnonymousRedirect()}</Root>;
		}

		if (formData) {
			const { initialFieldValues, proforma } = formData;
			if (proforma && proforma.formExists && !proforma.formValid) {
				return (
					<RequestTypeProformaError
						requestTypeId={requestTypeId}
						portalId={portalId}
						admin={canAdministerJira}
						formValidationResult={proforma.formValidationResult}
					/>
				);
			}
			return (
				<Root>
					{(this.hasRequiredFields() || this.hasRequiredFieldsInProformaForm()) && (
						<FormHeader>
							<Flex justifyContent="space-between">
								<Box>
									{intl.formatMessage(messages.instructionForMandatoryField)}
									<RequiredAsterisk />
								</Box>
							</Flex>
						</FormHeader>
					)}
					{FeatureGates.checkGate('render_create_request_field_with_loading_cursor') ? (
						<DisableViewUntilMount name={'request-create-form'}>
							<Formik initialValues={initialFieldValues} onSubmit={this.onSubmit}>
								{this.renderForm}
							</Formik>
						</DisableViewUntilMount>
					) : (
						<Formik initialValues={initialFieldValues} onSubmit={this.onSubmit}>
							{this.renderForm}
						</Formik>
					)}
				</Root>
			);
		}

		return null;
	}
}

export interface ConnectedProps {
	portalId?: string | number;
	requestTypeId?: string | number;
}

const selector = createSelector(
	(state: State, props: ConnectedProps) => {
		const accountId = getAccountId(state);
		const portalId = toNumber(props.portalId);
		const requestTypeId = toNumber(props.requestTypeId);
		const xsrfToken = getXsrfToken(state);
		const contactLink = getContactLink(state);
		const userAuthenticated = isLoggedIn(state);
		const canAdministerJira = getCanAdministerJIRA(state);
		const locale = getLocale(state).trim();

		let formData;
		let projectId;
		let dateFormat;
		let dateTimeFormat;
		let canCreateUsers = false;
		let firstDay;
		let useISO8601WeekNumbers;
		let hasProformaForm;
		let proformaFormIsDirty = false;
		let error = false;
		let projectKey;
		let isProjectAdmin = false;
		let isProjectSimplified;

		let isPrefillPending = false;

		if (portalId && requestTypeId) {
			const requestData = getRequestCreateOrError(state, portalId, requestTypeId);
			formData = requestData && 'error' in requestData ? undefined : requestData;
			error = !!(requestData && 'error' in requestData) ? true : false;
			canCreateUsers = getCanCreateUsers(state, portalId, requestTypeId);
			dateFormat = getDateFormat(state, portalId, requestTypeId);
			dateTimeFormat = getDateTimeFormat(state, portalId, requestTypeId);
			firstDay = getFirstDay(state, portalId, requestTypeId);
			useISO8601WeekNumbers = getUseISO8601WeekNumbers(state, portalId, requestTypeId);
			projectId = toString(getProjectId(state, portalId, requestTypeId));
			hasProformaForm = getRequestTypeHasProformaForm(state, portalId, requestTypeId);
			proformaFormIsDirty = getIsProformaFormDirty(state, portalId, requestTypeId);
			if (fg('jsm_portal_rovo_integration')) {
				isPrefillPending = getIsPrefillPending(state, portalId, requestTypeId);
			}
			projectKey = getProjectKey(state, portalId);
			isProjectSimplified = getIsProjectSimplified(state, portalId);
			isProjectAdmin = getCanAdministerProject(state, portalId) ?? false;
		}

		const analyticsPayload = getIssueAnalyticsContext(state, portalId);

		return {
			accountId,
			portalId,
			requestTypeId,
			formData,
			xsrfToken,
			projectId,
			contactLink,
			canCreateUsers,
			dateFormat,
			dateTimeFormat,
			firstDay,
			useISO8601WeekNumbers,
			userAuthenticated,
			analyticsPayload,
			hasProformaForm,
			proformaFormIsDirty,
			canAdministerJira,
			locale,
			error,
			projectKey,
			isProjectSimplified,
			isProjectAdmin,
			isPrefillPending,
		};
	},
	(requestFormData) => requestFormData,
);

const RequestCreateFormWithAnalyticsNextOld = connect(selector, {
	fetchRequestCreate: fetchRequestCreateAction,
	shareRequestAction: shareRequest,
	showSuccessFlagAction: showSuccessFlag,
	showWarningFlagAction: showWarningFlag,
})(
	withAnalyticsContext({
		source: 'portalScreen',
	})(withAnalyticsEvents()(withConnectFieldSupport(injectIntl(RequestCreateForm)))),
);

const RequestCreateFormWithAnalyticsNextNew = connect(selector, {
	fetchRequestCreate: fetchRequestCreateAction,
	shareRequestAction: shareRequest,
	showSuccessFlagAction: showSuccessFlag,
	showWarningFlagAction: showWarningFlag,
})(
	withAnalyticsContext({
		source: 'portalScreen',
	})(
		withAnalyticsEvents()(
			withConnectFieldSupport(injectIntl(withUiModificationsFormDecorator()(RequestCreateForm))),
		),
	),
);

export default componentWithCondition(
	isUiModificationsSupportEnabled,
	RequestCreateFormWithAnalyticsNextNew,
	RequestCreateFormWithAnalyticsNextOld,
);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Root = styled.div`
	position: relative;
	/* We're setting min height here so when the spinner is loaded it is displayed
    in at least something that is 50 grid units high. */
	min-height: ${grid.multiple(50).px};
`;
